import React, { useEffect, useState, useRef, useCallback } from "react"
import styles from "./index.module.scss"
import { Modal } from "@/components/Modal"
import { useShareStore } from "@/store/share"
import { ShareChannels } from "@/components/ShareChannels"
import { drawQRCode } from "@/Topics/Recall/Home/Panel/Share/draw"
import { adapter, useUser } from "@hg/recall-frame"
import { getInviteCode } from "@/utils/sdk"
import { downloadShareImage } from "@/utils/share"
import { useRoleInfo } from "@/user/session"
import { loadImageAnonymous } from "@/utils/load"
import { useGameStore, BOARD_SIZE } from "@/store/game"
import { i18n } from "@/i18n"
import { numberToChinese } from "@/utils/utils"
import BGSrc from "@/assets/imgs/common/main_share_modal_bg.jpg"
import winnerNianSrc from "@/assets/imgs/common/winner_nian.png"
import winnerWangSrc from "@/assets/imgs/common/winner_wang.png"
import boardBg from "@/assets/imgs/game/board.png"
import infoContainerBg from "@/assets/imgs/game/info_container.png"
import winnerBg from "@/assets/imgs/game/winner_bg.png"
import winnerTitle from "@/assets/imgs/game/winner_title.png"
import captureBlackTop from "@/assets/imgs/game/capture_black_top.png"
import captureWhiteTop from "@/assets/imgs/game/capture_white_top.png"
import captureBottom from "@/assets/imgs/game/capture_bottom.png"
import dividerImg from "@/assets/imgs/game/divider.png"
import winnerTextBg from "@/assets/imgs/game/winner_text.svg"
import winnerDotIcon from "@/assets/imgs/game/icon.svg"
import blackChess from "@/assets/imgs/game/black_chess.png"
import whiteChess from "@/assets/imgs/game/white_chess.png"
import { useRewardLottery } from "@/components/Reward"
import qrcode from "@/assets/qrcode.png"

interface Props {
    open: boolean
    onClose: () => void
}

// 按 contain 模式绘制图片（保持宽高比，不裁剪）
function drawImageContain(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number
) {
    const imgRatio = img.width / img.height
    const targetRatio = width / height

    let drawWidth: number
    let drawHeight: number
    let drawX: number
    let drawY: number

    if (imgRatio > targetRatio) {
        // 图片更宽，按宽度缩放
        drawWidth = width
        drawHeight = width / imgRatio
        drawX = x
        drawY = y + (height - drawHeight) / 2
    } else {
        // 图片更高，按高度缩放
        drawHeight = height
        drawWidth = height * imgRatio
        drawX = x + (width - drawWidth) / 2
        drawY = y
    }

    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
}

// 固定设计尺寸，与 draw.ts 一致：不依赖视口/横竖屏，保证导出图片宽高比正确
const DESIGN_WIDTH = 1241
const DESIGN_HEIGHT = 697

// 绘制分享图片的函数（使用固定画布尺寸，避免横屏时宽高比错误）
const drawShareImage = async ({
    text,
    qrCodeUrl,
    winner,
}: {
    text?: string
    qrCodeUrl?: string
    winner: string | undefined
}): Promise<{ canvas: HTMLCanvasElement; imageUrl: string } | null> => {
    const canvas = document.createElement("canvas")
    const width = DESIGN_WIDTH
    const height = DESIGN_HEIGHT

    // 获取设备像素比（DPR），解决 Retina 等高 DPR 设备模糊问题
    const dpr = window.devicePixelRatio || 1

    // 设置 canvas 的实际尺寸（物理像素）= 设计尺寸 × DPR
    canvas.width = width * dpr
    canvas.height = height * dpr

    const ctx = canvas.getContext("2d")
    if (!ctx || !canvas) {
        return null
    }

    // 缩放 context，这样绘制时使用的坐标仍然是设计像素
    ctx.scale(dpr, dpr)

    // 设置 canvas 的 CSS 显示尺寸（设计尺寸）
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`

    ctx.clearRect(0, 0, width, height)

    // 加载并绘制背景
    try {
        const bg = await loadImageAnonymous(BGSrc)
        if (bg) {
            ctx.drawImage(bg, 0, 0, width, height)
        }
    } catch (error) {
        console.error("背景图加载失败", error)
    }

    // 预加载棋盘和右侧信息面板相关的图片资源
    let boardTexture: HTMLImageElement | null = null
    let infoBg: HTMLImageElement | null = null
    let winnerBgImg: HTMLImageElement | null = null
    let winnerTitleImg: HTMLImageElement | null = null
    let captureBlackTopImg: HTMLImageElement | null = null
    let captureWhiteTopImg: HTMLImageElement | null = null
    let captureBottomImg: HTMLImageElement | null = null
    let dividerImgEl: HTMLImageElement | null = null
    let winnerTextBgImg: HTMLImageElement | null = null
    let winnerDotIconImg: HTMLImageElement | null = null
    let blackStoneImg: HTMLImageElement | null = null
    let whiteStoneImg: HTMLImageElement | null = null

    try {
        ;[
            boardTexture,
            infoBg,
            winnerBgImg,
            winnerTitleImg,
            captureBlackTopImg,
            captureWhiteTopImg,
            captureBottomImg,
            dividerImgEl,
            winnerTextBgImg,
            winnerDotIconImg,
            blackStoneImg,
            whiteStoneImg,
        ] = await Promise.all([
            loadImageAnonymous(boardBg).catch(() => null),
            loadImageAnonymous(infoContainerBg).catch(() => null),
            loadImageAnonymous(winnerBg).catch(() => null),
            loadImageAnonymous(winnerTitle).catch(() => null),
            loadImageAnonymous(captureBlackTop).catch(() => null),
            loadImageAnonymous(captureWhiteTop).catch(() => null),
            loadImageAnonymous(captureBottom).catch(() => null),
            loadImageAnonymous(dividerImg).catch(() => null),
            loadImageAnonymous(winnerTextBg).catch(() => null),
            loadImageAnonymous(winnerDotIcon).catch(() => null),
            loadImageAnonymous(blackChess).catch(() => null),
            loadImageAnonymous(whiteChess).catch(() => null),
        ])
    } catch {
        // 单个资源加载失败不影响整体渲染，忽略错误
    }

    // 直接用 canvas 绘制当前棋盘与得分信息，完全摆脱 html2canvas 依赖
    const {
        board,
        winner: gameWinner,
        scoreDiff,
        blackCaptures,
        whiteCaptures,
    } = useGameStore.getState()

    // ===== 绘制棋盘（使用与页面相同的木纹棋盘底图 + 内边距） =====
    const boardX = 200
    const boardY = 80
    const boardSize = 440

    const padTop = 30
    const padRight = 32
    const padBottom = 40
    const padLeft = 32

    const gridWidth = boardSize - padLeft - padRight
    const gridHeight = boardSize - padTop - padBottom
    const gridStartX = boardX + padLeft
    const gridStartY = boardY + padTop

    const cellWidth = gridWidth / (BOARD_SIZE - 1)
    const cellHeight = gridHeight / (BOARD_SIZE - 1)

    // 棋盘背景（木纹图）
    if (boardTexture) {
        drawImageContain(
            ctx,
            boardTexture,
            boardX,
            boardY,
            boardSize,
            boardSize
        )
    } else {
        // 兜底：纯色棋盘背景
        ctx.fillStyle = "#f6d8aa"
        ctx.fillRect(boardX, boardY, boardSize, boardSize)
    }

    // 网格线（限制在内边距之内）
    ctx.strokeStyle = "rgba(0, 0, 0, 0.4)"
    ctx.lineWidth = 2
    ctx.beginPath()
    for (let i = 0; i < BOARD_SIZE; i++) {
        // 横线
        ctx.moveTo(gridStartX, gridStartY + i * cellHeight)
        ctx.lineTo(gridStartX + gridWidth, gridStartY + i * cellHeight)
        // 竖线
        ctx.moveTo(gridStartX + i * cellWidth, gridStartY)
        ctx.lineTo(gridStartX + i * cellWidth, gridStartY + gridHeight)
    }
    ctx.stroke()

    // 星位（与页面保持一致）
    const starPoints = [
        [2, 2],
        [2, 6],
        [4, 4],
        [6, 2],
        [6, 6],
    ]
    ctx.fillStyle = "#8b6a3c"
    const starRadius = Math.min(cellWidth, cellHeight) * 0.12
    starPoints.forEach(([r, c]) => {
        const cx = gridStartX + c * cellWidth
        const cy = gridStartY + r * cellHeight
        ctx.beginPath()
        ctx.arc(cx, cy, starRadius, 0, Math.PI * 2)
        ctx.fill()
    })

    // 棋子（使用与实际页面相同的棋子图片）
    const stoneSize = Math.min(cellWidth, cellHeight) * 0.9
    for (let r = 0; r < BOARD_SIZE; r++) {
        for (let c = 0; c < BOARD_SIZE; c++) {
            const value = board[r][c]
            if (!value) continue

            const cx = gridStartX + c * cellWidth
            const cy = gridStartY + r * cellHeight
            const drawX = cx - stoneSize / 2
            const drawY = cy - stoneSize / 2

            if (value === "black" && blackStoneImg) {
                ctx.drawImage(blackStoneImg, drawX, drawY, stoneSize, stoneSize)
            } else if (value === "white" && whiteStoneImg) {
                ctx.drawImage(whiteStoneImg, drawX, drawY, stoneSize, stoneSize)
            } else {
                // 兜底：渐变圆形棋子
                const radius = stoneSize / 2
                ctx.beginPath()
                const grd = ctx.createRadialGradient(
                    cx - radius / 3,
                    cy - radius / 3,
                    radius / 4,
                    cx,
                    cy,
                    radius
                )
                if (value === "black") {
                    grd.addColorStop(0, "#555")
                    grd.addColorStop(1, "#000")
                } else {
                    grd.addColorStop(0, "#fff")
                    grd.addColorStop(1, "#ddd")
                }
                ctx.fillStyle = grd
                ctx.arc(cx, cy, radius, 0, Math.PI * 2)
                ctx.fill()
                ctx.strokeStyle = "rgba(0,0,0,0.25)"
                ctx.lineWidth = 1
                ctx.stroke()
            }
        }
    }

    // ===== 绘制得分信息（右侧面板，使用与页面相同的 info_container 底图） =====
    // 适当加宽信息面板，让中间内容显得更宽一些，同时保持在原来的中心位置
    const scorePanelCenterX = 680 + 470 / 2
    const scorePanelWidth = 510
    const scorePanelX = scorePanelCenterX - scorePanelWidth / 2
    const scorePanelY = 160
    const scorePanelHeight = 340

    // 信息面板底图
    if (infoBg) {
        drawImageContain(
            ctx,
            infoBg,
            scorePanelX,
            scorePanelY,
            scorePanelWidth,
            scorePanelHeight
        )
    } else {
        // 兜底：淡色背景
        ctx.fillStyle = "rgba(255, 248, 235, 0.9)"
        ctx.fillRect(
            scorePanelX,
            scorePanelY,
            scorePanelWidth,
            scorePanelHeight
        )
    }

    // ===== 标题区域：谁获胜（使用 winner_bg / winner_title） =====
    const isBlackWinner = gameWinner === "black"
    const isWhiteWinner = gameWinner === "white"
    const winnerText =
        gameWinner === "black"
            ? i18n.get("game.blackChess")
            : gameWinner === "white"
              ? i18n.get("game.whiteChess")
              : ""
    const winLabel = i18n.get("game.win")
    const pointLabel = i18n.get("game.point")
    const captureLabel = i18n.get("game.captureCount")

    // 信息面板内容整体向下偏移，让其在面板内更垂直居中
    const panelContentOffsetY = 32

    // 标题底图（整块 banner）
    const titleWidth = 270
    const titleHeight = 70
    const titleX = scorePanelX + (scorePanelWidth - titleWidth) / 2
    const titleY = scorePanelY + 26 + panelContentOffsetY
    if (winnerBgImg) {
        ctx.drawImage(winnerBgImg, titleX, titleY, titleWidth, titleHeight)
    }

    // 内容区域整体水平居中：左侧“黑子/白子”文字 + 右侧徽章
    const titleTextFontSize = 40
    ctx.font = `${titleTextFontSize}px SourceHanSansJP-VF, sans-serif`
    const winnerTextMetrics = ctx.measureText(winnerText || "")
    const textWidth = winnerTextMetrics.width
    const spacing = 10 // 文本和徽章之间的间距

    // “胜”徽章（背景图）
    const winBadgeSize = 76
    const contentWidth = (winnerText ? textWidth : 0) + spacing + winBadgeSize
    const contentStartX = titleX + (titleWidth - contentWidth) / 2
    const centerY = titleY + titleHeight / 2

    const textX = contentStartX
    const textY = centerY
    const winBadgeX = contentStartX + (winnerText ? textWidth : 0) + spacing
    const winBadgeY = centerY - winBadgeSize / 2

    if (winnerTitleImg) {
        ctx.drawImage(
            winnerTitleImg,
            winBadgeX,
            winBadgeY,
            winBadgeSize,
            winBadgeSize
        )
    }

    // 标题文字：左侧“黑子/白子”
    if (winnerText) {
        ctx.fillStyle = "#248190"
        ctx.font = `${titleTextFontSize}px SourceHanSansJP-VF, sans-serif`
        ctx.textAlign = "left"
        ctx.textBaseline = "middle"
        ctx.fillText(winnerText, textX, textY)
    }

    // 徽章内部的“胜”字，居中绘制
    const titleBadgeCenterX = winBadgeX + winBadgeSize / 2
    const titleBadgeCenterY = centerY
    ctx.fillStyle = "#c93d1e"
    const titleBadgeFontSize = 56
    ctx.font = `${titleBadgeFontSize}px SourceHanSansJP-VF, sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    // 简单的白色描边/发光效果，模拟 CSS 文本描边
    ctx.shadowColor = "rgba(255,255,255,0.9)"
    ctx.shadowBlur = 6
    ctx.fillText(winLabel, titleBadgeCenterX, titleBadgeCenterY)
    ctx.shadowBlur = 0

    // ===== 目数差一行："胜"圆徽 + 小 icon + "二十五" + "子" =====
    const diffAbs = Math.abs(scoreDiff || 0)
    const diffNumberText = numberToChinese(diffAbs)
    const diffUnitText = pointLabel // "子"

    // 字体与样式参考 .scoreText / .scoreTextUnit
    const scoreFontSize = 22
    ctx.font = `${scoreFontSize}px SourceHanSansJP-VF, sans-serif`
    const numberMetrics = ctx.measureText(diffNumberText)
    const unitMetrics = ctx.measureText(diffUnitText)

    const scoreIconSize = 48
    const dotIconSize = 8
    const gap1 = 8 // 徽章与小圆点间距
    const gap2 = 8 // 小圆点与文字间距

    const rowWidth =
        scoreIconSize +
        gap1 +
        dotIconSize +
        gap2 +
        numberMetrics.width +
        unitMetrics.width
    const rowCenterX = scorePanelX + scorePanelWidth / 2
    const rowY = scorePanelY + 120 + panelContentOffsetY // 整行位置稍微偏下
    let cursorX = rowCenterX - rowWidth / 2

    // 左侧圆形 "胜" 徽章（winner_text.svg 背景 + 文字）
    if (winnerTextBgImg) {
        ctx.drawImage(
            winnerTextBgImg,
            cursorX,
            rowY - scoreIconSize / 2,
            scoreIconSize,
            scoreIconSize
        )
    }
    // 圆徽中的 "胜"
    ctx.fillStyle = "#fbf0d9"
    ctx.font = `${scoreFontSize}px SourceHanSansJP-VF, sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    const badgeCenterX = cursorX + scoreIconSize / 2
    const badgeCenterY = rowY
    ctx.fillText(winLabel, badgeCenterX, badgeCenterY)

    cursorX += scoreIconSize + gap1

    // 中间小 icon（红色小点）
    if (winnerDotIconImg) {
        ctx.drawImage(
            winnerDotIconImg,
            cursorX,
            rowY - dotIconSize / 2,
            dotIconSize,
            dotIconSize
        )
    }
    cursorX += dotIconSize + gap2

    // 右侧文字："二十五" + "子"
    ctx.textAlign = "left"
    ctx.textBaseline = "middle"

    // 数字部分：红色，带浅色描边
    ctx.font = `${scoreFontSize}px SourceHanSansJP-VF, sans-serif`
    ctx.lineWidth = 4
    ctx.strokeStyle = "#fbf0d9"
    ctx.fillStyle = "#c03b1b"
    ctx.strokeText(diffNumberText, cursorX, rowY)
    ctx.fillText(diffNumberText, cursorX, rowY)

    const unitX = cursorX + numberMetrics.width

    // 单位 "子"：棕金色，描边同上
    ctx.strokeStyle = "#fbf0d9"
    ctx.fillStyle = "#b99d7e"
    ctx.strokeText(diffUnitText, unitX, rowY)
    ctx.fillText(diffUnitText, unitX, rowY)

    // ===== 提子信息（使用 capture_* 系列底图） =====
    // 不再绘制中间分割线，直接在目数下方留出一定间距后绘制提子信息
    const captureBaseY = scorePanelY + 145 + panelContentOffsetY
    const captureTopSize = 84
    const captureBottomSize = 32
    const captureGapX = 30
    // 让两个提子信息块整体在得分面板内水平居中
    const capturesTotalWidth = captureTopSize * 2 + captureGapX
    const capturesStartX =
        scorePanelX + (scorePanelWidth - capturesTotalWidth) / 2

    // 黑方提子区域
    if (captureBlackTopImg && captureBottomImg) {
        const x = capturesStartX
        const topY = captureBaseY
        const isDisabled = isWhiteWinner

        if (isDisabled) {
            ctx.save()
            ctx.globalAlpha = 0.8
            ctx.filter = "grayscale(100%)"
        }
        ctx.drawImage(
            captureBlackTopImg,
            x,
            topY,
            captureTopSize,
            captureTopSize
        )
        // 底部提子数标签
        const bottomY = topY + captureTopSize - 30
        ctx.drawImage(
            captureBottomImg,
            x,
            bottomY,
            captureTopSize,
            captureBottomSize
        )
        ctx.fillStyle = "#fff"
        ctx.font = "18px SourceHanSansJP-VF, sans-serif"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(
            captureLabel,
            x + captureTopSize / 2,
            bottomY + captureBottomSize / 2
        )

        // 数字
        ctx.fillStyle = "#248190"
        ctx.font = "32px SourceHanSansJP-VF, sans-serif"
        ctx.fillText(
            String(blackCaptures).padStart(2, "0"),
            x + captureTopSize / 2,
            topY + 100
        )

        if (isDisabled) {
            ctx.restore()
        }
    }

    // 白方提子区域
    if (captureWhiteTopImg && captureBottomImg) {
        const x = capturesStartX + captureTopSize + captureGapX
        const topY = captureBaseY
        const isDisabled = isBlackWinner

        if (isDisabled) {
            ctx.save()
            ctx.globalAlpha = 0.8
            ctx.filter = "grayscale(100%)"
        }
        ctx.drawImage(
            captureWhiteTopImg,
            x,
            topY,
            captureTopSize,
            captureTopSize
        )
        const bottomY = topY + captureTopSize - 30
        ctx.drawImage(
            captureBottomImg,
            x,
            bottomY,
            captureTopSize,
            captureBottomSize
        )
        ctx.fillStyle = "#fff"
        ctx.font = "18px SourceHanSansJP-VF, sans-serif"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(
            captureLabel,
            x + captureTopSize / 2,
            bottomY + captureBottomSize / 2
        )

        ctx.fillStyle = "#248190"
        ctx.font = "32px SourceHanSansJP-VF, sans-serif"
        ctx.fillText(
            String(whiteCaptures).padStart(2, "0"),
            x + captureTopSize / 2,
            topY + 100
        )

        if (isDisabled) {
            ctx.restore()
        }
    }

    // 绘制文本 (left: 230px, top: 552px, width: 450px, height: 80px)
    // top = 697 - 70 - 80 = 547px，向下移动5px后为552px
    if (winner && text) {
        // 根据胜者绘制对话框背景图
        try {
            const dialogBgSrc =
                winner === "nian" ? winnerNianSrc : winnerWangSrc
            const dialogBg = await loadImageAnonymous(dialogBgSrc)
            if (dialogBg) {
                // left: 100, bottom: 50
                // 计算 top 位置：height - 50 - dialogBg.height
                const dialogLeft = winner === "nian" ? 20 : 10
                const dialogTop = winner === "nian" ? 440 : 460
                ctx.drawImage(
                    dialogBg,
                    dialogLeft,
                    dialogTop,
                    dialogBg.width * 0.7,
                    dialogBg.height * 0.7
                )
            }
        } catch (error) {
            console.error("对话框背景图加载失败", error)
        }

        // 绘制文字
        const defaultFont = "sans-serif"
        let font: string
        try {
            await document.fonts.ready
            font = "SourceHanSansJP-VF"
        } catch {
            font = defaultFont
        }

        ctx.font = `100 22px ${font}`
        ctx.fillStyle = "#4d4d4d"
        ctx.textAlign = "left"
        ctx.textBaseline = "top"

        // 文本换行处理
        const maxWidth = 450
        const lineHeight = 28
        const x = 230
        let y = 560 // 向下移动5px
        const maxY = 547 + 80

        // 按字符分割，支持中文
        const chars = Array.from(text)
        let line = ""

        for (let i = 0; i < chars.length; i++) {
            const testLine = line + chars[i]
            const metrics = ctx.measureText(testLine)
            if (metrics.width > maxWidth && line.length > 0) {
                ctx.fillText(line, x, y)
                line = chars[i]
                y += lineHeight
                // 限制在指定高度内
                if (y + lineHeight > maxY) {
                    break
                }
            } else {
                line = testLine
            }
        }
        if (line && y + lineHeight <= maxY) {
            ctx.fillText(line, x, y)
        }
    }

    // 绘制二维码 (100px × 100px, left: 991px, top: 539px)
    // left = 1241 - 150 - 100 = 991px, top = 697 - 58 - 100 = 539px
    if (qrCodeUrl) {
        try {
            const qrImg = await loadImageAnonymous(qrcode)
            if (qrImg) {
                drawImageContain(ctx, qrImg, 1017, 551, 105, 105)
            }
        } catch (error) {
            console.error("二维码加载失败", error)
        }
    }

    const imageUrl = canvas.toDataURL("image/jpeg")
    return { canvas, imageUrl }
}

const ResultShareModal = ({ open, onClose }: Props) => {
    // 从 store 中获取存储的数据
    const { text, dialogueData } = useShareStore()
    const role = useRoleInfo()
    const [qrCodeUrl, setQrCodeUrl] = useState<string>("")
    const [shareImageUrl, setShareImageUrl] = useState<string>("")
    const [shareCanvas, setShareCanvas] = useState<HTMLCanvasElement | null>(
        null
    )
    const [isImageReady, setIsImageReady] = useState<boolean>(false)
    const [hasShared, setHasShared] = useState<boolean>(false)
    const modalRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const shareImageRef = useRef<HTMLImageElement>(null)
    const lotterySDK = useRewardLottery()

    // Modal 打开时清空图片，避免显示旧图片导致闪烁
    useEffect(() => {
        if (open) {
            setShareImageUrl("")
            setShareCanvas(null)
            setQrCodeUrl("")
            setIsImageReady(false)
        } else {
            // Modal 关闭时重置状态
            setIsImageReady(false)
        }
    }, [open])

    // 生成二维码
    useEffect(() => {
        if (!open) return

        const generateQRCode = async () => {
            // 获取相关参数
            const code = getInviteCode()
            const uid = role?.userId

            // 构建二维码内容URL
            const qrContent =
                location.origin +
                location.pathname +
                `?${(
                    [
                        ["invite_code", code],
                        ["share_type", "qrCode"],
                        ["source_from", adapter.source.from],
                        ["share_by", uid ? window.btoa(String(uid)) : ""],
                    ] as const
                )
                    .filter(([, value]) => !!value)
                    .map((group) => group.join("="))
                    .join("&")}`

            // 生成二维码
            const qrCanvas = await drawQRCode({
                size: 100,
                content: qrContent,
            })

            // 转换为图片URL
            const qrUrl = qrCanvas.toDataURL("image/png")
            setQrCodeUrl(qrUrl)
        }

        generateQRCode()
    }, [open, role?.userId])

    // 生成分享图片
    useEffect(() => {
        // 注意：这里需要 open 为 true 才能获取 modalRef 的尺寸
        // 但 Modal 的实际显示由 open && isImageReady 控制
        if (!open || !qrCodeUrl) {
            // 如果条件不满足，清空图片（避免显示旧图片）
            if (!open) {
                setShareImageUrl("")
            }
            return
        }

        let isCancelled = false

        const generateShareImage = async () => {
            // 使用固定设计尺寸绘制（与 draw.ts 一致），不依赖 modal 的 getBoundingClientRect，
            // 避免横屏时页面旋转导致 canvas/导出图片宽高比错误
            const result = await drawShareImage({
                text,
                qrCodeUrl,
                winner: dialogueData?.[0]?.speaker,
            })

            // 检查是否已被取消（Modal 已关闭或依赖项已变化）
            // 只有在 Modal 仍然打开且未被取消时才更新图片
            if (!isCancelled && result && open) {
                const { canvas, imageUrl } = result
                // 预加载图片，等待完全加载后再显示，避免逐步渲染
                const img = new Image()
                img.onload = () => {
                    if (!isCancelled && open) {
                        setShareCanvas(canvas)
                        setShareImageUrl(imageUrl)
                        // 使用 requestAnimationFrame 确保在下一帧显示，让浏览器完成渲染
                        requestAnimationFrame(() => {
                            if (!isCancelled && open) {
                                setIsImageReady(true)
                            }
                        })
                    }
                }
                img.onerror = () => {
                    // 即使预加载失败，也显示图片（让浏览器自己处理）
                    if (!isCancelled && open) {
                        setShareCanvas(canvas)
                        setShareImageUrl(imageUrl)
                        setIsImageReady(true)
                    }
                }
                img.src = imageUrl
            }
        }

        // 延迟一下确保DOM已渲染
        const timer = setTimeout(() => {
            generateShareImage()
        }, 100)

        return () => {
            isCancelled = true
            clearTimeout(timer)
        }
    }, [open, qrCodeUrl, text, dialogueData?.[0]?.speaker])

    const user = useUser()
    const code = getInviteCode()

    const download = () => {
        downloadShareImage(shareImageUrl, "game")
    }

    // 绘制canvas内容的函数
    const drawCanvas = useCallback(
        (canvas: HTMLCanvasElement, sourceCanvas: HTMLCanvasElement) => {
            const ctx = canvas.getContext("2d")
            if (!ctx) return

            // 设置目标canvas的实际尺寸（物理像素），与源canvas保持一致
            // 这样能保持高 DPR 的清晰度
            canvas.width = sourceCanvas.width
            canvas.height = sourceCanvas.height

            // CSS 尺寸由 .shareCanvas 类控制（width: 100%; height: 100%），不需要手动设置

            // 清空目标canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            // 绘制源canvas内容（直接绘制，因为尺寸已经匹配）
            ctx.drawImage(sourceCanvas, 0, 0)
        },
        []
    )

    // 将canvas内容绘制到显示的canvas上的回调函数
    const handleCanvasRef = useCallback(
        (canvas: HTMLCanvasElement | null) => {
            if (canvas && shareCanvas) {
                drawCanvas(canvas, shareCanvas)
            }
        },
        [shareCanvas, drawCanvas]
    )

    // 当shareCanvas更新时，重新绘制
    useEffect(() => {
        if (canvasRef.current && shareCanvas) {
            drawCanvas(canvasRef.current, shareCanvas)
        }
    }, [shareCanvas, drawCanvas])

    const handleClose = () => {
        lotterySDK?.lotteryOnly()
        onClose()
    }

    return (
        <Modal
            modalClass={styles.shareModal}
            open={open}
            onClose={handleClose}
            contentClass={styles.shareContent}
            showConfirmBtn={false}
            maskClosable={false}
        >
            {/* 只有在图片准备好后才显示内容，避免关闭按钮提前显示 */}
            {isImageReady ? (
                <>
                    <div ref={modalRef} className={styles.modalContent}>
                        {/* 显示canvas高清内容 */}
                        {shareCanvas && (
                            <canvas
                                ref={(canvas) => {
                                    canvasRef.current = canvas
                                    handleCanvasRef(canvas)
                                }}
                                className={styles.shareCanvas}
                            />
                        )}
                        {/* 覆盖透明图片用于长按保存 */}
                        {shareImageUrl && (
                            <img
                                ref={shareImageRef}
                                className={styles.shareImage}
                                src={shareImageUrl}
                                alt="分享图片"
                                decoding="sync"
                                loading="eager"
                            />
                        )}
                    </div>

                    <ShareChannels
                        uid={user?.uid}
                        nickname={user?.nickName}
                        src={shareImageUrl}
                        shareImageRef={shareImageRef}
                        code={code}
                        download={download}
                        onHasSharedChange={setHasShared}
                    />
                </>
            ) : (
                // 图片生成中，隐藏内容但保留 ref 用于获取尺寸
                <div
                    ref={modalRef}
                    className={styles.modalContent}
                    style={{ visibility: "hidden" }}
                />
            )}
        </Modal>
    )
}

export default ResultShareModal
